/**
 * Invite / add-user helpers for Settings → Users.
 * Keep membership checks here so the Users screen does not open the
 * "existing OMS account" modal for someone already on this venue.
 */

import { VENUE_ADMIN } from './roles'
import {
  VENUE_ROLE_CAPABILITY_SHORT_LABELS,
  getEnabledCapabilityChips,
  resolveVenueRole,
} from './venueRoleCapabilities'

export const USER_ALREADY_ON_VENUE_FALLBACK =
  'This person is already on this venue.'

export const CANNOT_INVITE_SELF_COPY =
  "You can't invite your own login. Invite someone else, or ask another Admin to change your role."

export const ADMIN_INVITE_ACCESS_COPY =
  "They'll be able to place orders, create recipes, and access all venue features"

export const normalizeInviteEmail = email =>
  String(email || '')
    .trim()
    .toLowerCase()

export const looksLikeEmail = value =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())

/**
 * Browsers often autofill the logged-in (inviter) email into the name field.
 * Clear anything that looks like a non-invitee email so we never keep it.
 */
export const scrubAutofilledInviterEmail = (
  value,
  inviteeEmail,
  inviterEmail = '',
) => {
  const trimmed = String(value || '').trim()
  if (!trimmed) return ''
  const invitee = normalizeInviteEmail(inviteeEmail)
  const inviter = normalizeInviteEmail(inviterEmail)
  const asEmail = normalizeInviteEmail(trimmed)
  if (looksLikeEmail(trimmed) && asEmail !== invitee) return ''
  if (inviter && asEmail === inviter) return ''
  return trimmed
}

export const isValidInviteName = name => {
  const trimmed = String(name || '').trim()
  // Blank is allowed — resolveInvitedUserName falls back to the invitee email.
  if (!trimmed) return true
  return trimmed.length >= 2
}

/**
 * Resolve the display name stored on an invited Account.
 * Never keep a different email (e.g. the inviter's autofilled address) as the
 * invited user's name — fall back to the invitee's own email instead.
 */
export const resolveInvitedUserName = (name, inviteeEmail) => {
  const trimmedName = String(name || '').trim()
  const email = normalizeInviteEmail(inviteeEmail)
  if (!trimmedName) return email
  if (
    looksLikeEmail(trimmedName) &&
    normalizeInviteEmail(trimmedName) !== email
  ) {
    return email
  }
  return trimmedName
}

export const getInviteNameValidationError = (name, inviteeEmail) => {
  const trimmed = String(name || '').trim()
  // Blank is fine — the invitee email is used as the display name.
  if (!trimmed) return null
  if (trimmed.length < 2) {
    return 'Please enter the full name (at least 2 characters), or leave blank to use their email.'
  }
  if (
    looksLikeEmail(trimmed) &&
    normalizeInviteEmail(trimmed) !== normalizeInviteEmail(inviteeEmail)
  ) {
    return "Please enter the person's name, not an email address."
  }
  return null
}

const memberEmailsFromUser = user => {
  if (!user || typeof user !== 'object') return []
  return [user.emailAddress, user.email].filter(Boolean)
}

export const getVenueMemberEmails = venue => {
  const emails = []
  memberEmailsFromUser(venue?.account).forEach(email => emails.push(email))
  const additional = Array.isArray(venue?.additionalUsers)
    ? venue.additionalUsers
    : []
  additional.forEach(user => {
    memberEmailsFromUser(user).forEach(email => emails.push(email))
  })
  return emails
}

export const isEmailAlreadyOnVenue = (email, venue) => {
  const target = normalizeInviteEmail(email)
  if (!target) return false
  return getVenueMemberEmails(venue).some(
    member => normalizeInviteEmail(member) === target,
  )
}

export const isInvitingOwnAccount = (email, account) => {
  const target = normalizeInviteEmail(email)
  if (!target) return false
  return memberEmailsFromUser(account).some(
    member => normalizeInviteEmail(member) === target,
  )
}

export const getAlreadyOnVenueMessage = email => {
  const trimmed = String(email || '').trim()
  if (!trimmed) return USER_ALREADY_ON_VENUE_FALLBACK
  return `${trimmed} is already on this venue.`
}

export const isAlreadyOnVenueApiError = errorOrResponse => {
  const text = String(
    errorOrResponse?.response?.data?.message || errorOrResponse?.message || '',
  )
  return /already has access to this venue|already the primary owner of this venue/i.test(
    text,
  )
}

export const getAddUserToVenueErrorMessage = error =>
  error?.response?.data?.message ||
  error?.message ||
  'Failed to add user. Please try again.'

const joinInviteCapabilityLabels = labels => {
  if (labels.length === 0) return ''
  if (labels.length === 1) return labels[0]
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`
}

export const formatInviteCapabilityList = capabilities =>
  joinInviteCapabilityLabels(
    getEnabledCapabilityChips(capabilities).map(
      chip => VENUE_ROLE_CAPABILITY_SHORT_LABELS[chip.key] || chip.label,
    ),
  )

/**
 * Existing-user invite modal: Admin keeps the original “all venue features”
 * line. Limited roles name the role and list its ticks so we do not promise
 * Admin access for Kitchen Supervisor (or Chef / Order Only).
 */
export const getExistingUserInviteAccessCopy = (roleId, customRoles = []) => {
  const resolved = resolveVenueRole(roleId, customRoles)
  if (resolved.kind === 'system' && resolved.id === VENUE_ADMIN) {
    return ADMIN_INVITE_ACCESS_COPY
  }
  const list = formatInviteCapabilityList(resolved.capabilities)
  if (!list) return `They'll join as ${resolved.displayName}`
  return `They'll join as ${resolved.displayName}, with access to ${list}`
}
